import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import { InboxFilters, TimeFilter, InboxItem, matchesTimeFilter } from '../types';

// Time refresh interval: check every 10 seconds if display text changed
const TIME_REFRESH_INTERVAL_MS = 10000;

export interface InboxHeaderProps {
  totalCount: number;
  actionableCount: number;
  filters: InboxFilters;
  onFiltersChange: (filters: InboxFilters) => void;
  items: InboxItem[]; // Need items to calculate counts per filter
  lastRefreshTime?: Date; // When data was last refreshed
  isRefreshing?: boolean; // Whether currently refreshing
}

/**
 * Header component with title, time filters, and search
 * Redesigned for FBO operations with focus on "today" view
 */
export function InboxHeader({
  totalCount,
  actionableCount,
  filters,
  onFiltersChange,
  items,
  lastRefreshTime,
  isRefreshing = false,
}: InboxHeaderProps) {
  const { t } = useTranslation();
  const [now, setNow] = React.useState(new Date());

  // Update "time ago" display only when text actually changes
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (!lastRefreshTime) return;

      const currentText = getTimeAgo();
      const newNow = new Date();
      const seconds = Math.floor((newNow.getTime() - lastRefreshTime.getTime()) / 1000);

      let newText: string | null = null;
      if (seconds < 5) newText = t('screens.operationsInbox.header.refreshStatus.justNow');
      else if (seconds < 60) newText = t('screens.operationsInbox.header.refreshStatus.secondsAgo', { count: seconds });
      else {
        const minutes = Math.floor(seconds / 60);
        newText = minutes === 1 ? t('screens.operationsInbox.header.refreshStatus.minuteAgo') : t('screens.operationsInbox.header.refreshStatus.minutesAgo', { count: minutes });
      }

      // Only update state if display text changed
      if (newText !== currentText) {
        setNow(newNow);
      }
    }, TIME_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [lastRefreshTime, t]);

  const getTimeAgo = () => {
    if (!lastRefreshTime) return null;
    const seconds = Math.floor((now.getTime() - lastRefreshTime.getTime()) / 1000);
    if (seconds < 5) return t('screens.operationsInbox.header.refreshStatus.justNow');
    if (seconds < 60) return t('screens.operationsInbox.header.refreshStatus.secondsAgo', { count: seconds });
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) return t('screens.operationsInbox.header.refreshStatus.minuteAgo');
    return t('screens.operationsInbox.header.refreshStatus.minutesAgo', { count: minutes });
  };
  // Calculate counts for each time filter
  const todayCount = items.filter(i => matchesTimeFilter(i, 'today')).length;
  const tomorrowCount = items.filter(i => matchesTimeFilter(i, 'tomorrow')).length;
  const weekCount = items.filter(i => matchesTimeFilter(i, 'week')).length;
  const newTailsCount = items.filter(i => i.item_type === 'quarantine').length;

  // Get the current filter's count for display
  const getCurrentFilterLabel = () => {
    switch (filters.timeFilter) {
      case 'today':
        return todayCount === 0
          ? t('screens.operationsInbox.header.statusLabels.allSetToday')
          : t('screens.operationsInbox.header.statusLabels.arrivalsToday', { count: todayCount });
      case 'tomorrow':
        return tomorrowCount === 0
          ? t('screens.operationsInbox.header.statusLabels.nothingTomorrow')
          : t('screens.operationsInbox.header.statusLabels.arrivalsTomorrow', { count: tomorrowCount });
      case 'week':
        return weekCount === 0
          ? t('screens.operationsInbox.header.statusLabels.clearForWeek')
          : t('screens.operationsInbox.header.statusLabels.arrivalsWeek', { count: weekCount });
      case 'all':
        return actionableCount === 0
          ? t('screens.operationsInbox.header.statusLabels.allCaughtUp')
          : t('screens.operationsInbox.header.statusLabels.total', { count: actionableCount });
      default:
        return '';
    }
  };

  const handleTimeFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: TimeFilter | null) => {
    if (newFilter !== null) {
      onFiltersChange({ ...filters, timeFilter: newFilter });
    }
  };

  return (
    <Box>
      {/* Title Row */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <InboxIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
        <Typography variant="h5" fontWeight={600}>
          {t('screens.operationsInbox.title')}
        </Typography>

        {/* Context-aware status chip */}
        {filters.timeFilter === 'today' && todayCount === 0 ? (
          <Chip
            label={t('screens.operationsInbox.header.statusLabels.allSetToday')}
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ) : (
          <Chip
            label={getCurrentFilterLabel()}
            color={filters.timeFilter === 'today' && todayCount > 0 ? 'warning' : 'default'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Time Filter Tabs */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <ToggleButtonGroup
          value={filters.timeFilter}
          exclusive
          onChange={handleTimeFilterChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
              py: 0.5,
              borderRadius: '16px !important',
              border: '1px solid',
              borderColor: 'divider',
              '&:not(:first-of-type)': {
                marginLeft: '8px',
                borderLeft: '1px solid',
                borderLeftColor: 'divider',
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              },
            },
          }}
        >
          <ToggleButton value="today">
            {t('screens.operationsInbox.header.timeFilters.today')} {todayCount > 0 && `(${todayCount})`}
          </ToggleButton>
          <ToggleButton value="tomorrow">
            {t('screens.operationsInbox.header.timeFilters.tomorrow')} {tomorrowCount > 0 && `(${tomorrowCount})`}
          </ToggleButton>
          <ToggleButton value="week">
            {t('screens.operationsInbox.header.timeFilters.week')} {weekCount > 0 && `(${weekCount})`}
          </ToggleButton>
          <ToggleButton
            value="all"
            sx={newTailsCount > 0 && filters.timeFilter !== 'all' ? {
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { boxShadow: 'none' },
                '50%': { boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.3)' },
              },
            } : {}}
          >
            {t('screens.operationsInbox.header.timeFilters.all')} {newTailsCount > 0 && (
              <Chip
                label={t('screens.operationsInbox.header.newTails', { count: newTailsCount })}
                size="small"
                color="error"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 600 }}
              />
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Search Bar and Status */}
      <Box display="flex" alignItems="center" gap={2}>
        <TextField
          placeholder={t('screens.operationsInbox.header.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          size="small"
          sx={{
            width: 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'rgba(0, 0, 0, 0.02)',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Subtle auto-refresh status indicator */}
        {(isRefreshing || lastRefreshTime) && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              fontStyle: 'italic',
            }}
          >
            {isRefreshing
              ? t('screens.operationsInbox.header.refreshStatus.refreshing')
              : t('screens.operationsInbox.header.refreshStatus.updated', { timeAgo: getTimeAgo() })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
